<template>
  <div
    class="hospitalsDetailDepartmentTable"
    id="js-hospitalsDetailDepartmentTable"
    v-dragscroll.pass="!isTouchDevice()"
  >
    <table :style="tableWidth()">
      <thead>
        <tr>
          <th></th>
          <th>医師数</th>
          <th>指導医数</th>
          <th>平均<br />外来患者数</th>
          <th>平均<br />入院患者数</th>
          <th>関連医局情報</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, index) in data" :key="index">
          <td>{{ row.dept }}</td>
          <td>{{ row.resident_num ? `${row.resident_num}名` : '' }}</td>
          <td>{{ row.instructor_num ? `${row.instructor_num}名` : '' }}</td>
          <td>{{ row.outpatient_avg ? `${row.outpatient_avg}名／日` : '' }}</td>
          <td>{{ row.inpatient_avg ? `${row.inpatient_avg}名／日` : '' }}</td>
          <td>{{ row.affiliated_dept }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, PropType } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import ScrollHint from 'scroll-hint'
import '../../../node_modules/scroll-hint/css/scroll-hint.css'
import { isTouchDevice } from '@/utils/commonMethods'

type HospitalsDetailDepartmentTableProps = {
  dept: string
  resident_num: string
  instructor_num: string
  outpatient_avg: string
  inpatient_avg: string
  affiliated_dept: string
}
export default defineComponent({
  name: 'HospitalsDetailDepartmentTable',
  props: {
    data: {
      type: Array as PropType<HospitalsDetailDepartmentTableProps[]>,
      default: () => []
    }
  },

  setup(props) {
    const scrollAnime = onMounted(() => {
      new ScrollHint('.hospitalsDetailDepartmentTable', {
        i18n: {
          scrollable: '横スクロールで表示'
        }
      } as any)
    })

    // table幅
    const dataKeyNum = Object.keys(props.data[0]).length
    const tableWidth = () => {
      return {
        width: 114 * (dataKeyNum - 1) + 202 + 'px'
      }
    }

    onMounted(() => {
      const table = document.querySelector('#js-hospitalsDetailDepartmentTable')
      const tableHead = table?.querySelector('thead')
      let tableHeight = 0
      let tablePosition = 0
      let lastTime = 0

      const setTableInfo = () => {
        tableHeight = table ? table.clientHeight : 0
        tablePosition = table
          ? table.getBoundingClientRect().top + window.pageYOffset
          : 0
      }
      setTableInfo()

      const fixTableHeader = () => {
        const now = Date.now()
        if (now - lastTime < 10) return
        lastTime = now

        if (!tablePosition || !tableHeight) return

        if (
          window.pageYOffset >= tablePosition &&
          window.pageYOffset <= tablePosition + tableHeight
        ) {
          if (tableHead) {
            const diff = window.pageYOffset - tablePosition
            tableHead.style.top = diff + 'px'
          }
        } else {
          tableHead?.setAttribute('style', '')
        }
      }
      document.addEventListener('scroll', fixTableHeader)
      window.addEventListener('resize', setTableInfo)

      onBeforeRouteLeave(() => {
        document.removeEventListener('scroll', fixTableHeader)
        window.removeEventListener('resize', setTableInfo)
      })
    })

    return {
      tableWidth,
      scrollAnime,
      isTouchDevice
    }
  }
})
</script>

<style lang="scss" scoped>
@use 'src/styles/abstracts/mixins';
@use 'src/styles/abstracts/variables' as var;

.hospitalsDetailDepartmentTable {
  overflow-x: auto;
  padding-top: 60px;
  position: relative;
  cursor: grab;
  &:active {
    cursor: grabbing;
  }
  table {
    border-spacing: 0;
    margin-bottom: 24px;
    th,
    td {
      border-right: 1px solid var.$color-border-gray;
      border-bottom: 1px solid var.$color-border-gray;
      font-weight: normal;
      line-height: 1.5;
      width: 114px;
      text-align: center;
      &:last-child {
        width: 202px;
      }
      &:first-child {
        border-left: 1px solid var.$color-border-gray;
      }
    }

    thead {
      position: absolute;
      top: 0;
      z-index: 2;
      tr {
        display: flex;
      }
      th {
        background-color: var.$color-other-bg;
        height: 60px;
        border-top: 1px solid var.$color-border-gray;
        display: grid;
        place-content: center;
        &:first-child {
          background-color: #fff;
        }
      }
    }

    tbody {
      td {
        vertical-align: middle;
        padding: 10px 16px;
        &:first-child {
          background-color: var.$color-other-bg;
        }
        &:last-child {
          text-align: left;
        }
      }
    }

    th:first-child,
    td:first-child {
      position: sticky;
      left: 0;
      z-index: 1;
    }
  }
}

::v-deep(.scroll-hint-icon-wrap) {
  z-index: 3;
}

::v-deep(.scroll-hint-icon) {
  width: 130px;
  height: 110px;
  padding: 35px 0 10px;
  background-color: rgba(41, 204, 195, 0.9);
}

::v-deep(.scroll-hint-text) {
  font-size: 11px;
  font-weight: 700;
}
</style>
